import {
    ArrowLeftIcon,
    PhotoIcon,
    UserCircleIcon,
    ArrowUpTrayIcon,
    TrashIcon,
    DocumentTextIcon,
    CheckCircleIcon,
    ArrowPathIcon,
} from '@heroicons/react/20/solid'
import { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Field, Label, Switch, Description } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { useMutation, useQuery } from '@apollo/client'
import { CREATE_ADMIN_USER, UPDATE_ADMIN_USER } from 'graphql/users/mutations'
import { GET_ADMIN_USER_ROLES } from 'graphql/users/queries'
import { generateUniqueS3Key } from 'utils/common'
import { uploadData } from 'aws-amplify/storage'
import { ImageDisplay } from 'components/custom/ImageDisplay'
import { PhoneInput } from 'react-international-phone'
import 'react-international-phone/style.css'
import { PhoneNumberUtil } from 'google-libphonenumber'

const phoneUtil = PhoneNumberUtil.getInstance()

const isPhoneValid = phone => {
    try {
        return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone))
    } catch (error) {
        return false
    }
}

export default function UserForm() {
    const navigate = useNavigate()
    const location = useLocation()
    const { t, i18n } = useTranslation()
    const userData = location.state?.userData
    const isEditMode = !!userData?.id
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [createAdminUserError, setCreateAdminUserError] = useState(t('Problem Adding User'))
    const [submitLoading, setSubmitLoading] = useState(false)
    const [profileImage, setProfileImage] = useState(null)
    const [imageUploadError, setImageUploadError] = useState('')
    const [passportAttachment, setPassportAttachment] = useState(null)
    const [passportUploadError, setPassportUploadError] = useState('')
    const [passportUploading, setPassportUploading] = useState(false)
    const [nationalIdAttachment, setNationalIdAttachment] = useState(null)
    const [nationalIdUploadError, setNationalIdUploadError] = useState('')
    const [nationalIdUploading, setNationalIdUploading] = useState(false)

    // Fetch admin roles dynamically
    const { data: rolesData, loading: rolesLoading, error: rolesError } = useQuery(GET_ADMIN_USER_ROLES)

    // Helper function to get role description based on current language
    const getRoleDescription = role => {
        const currentLang = i18n.language || 'en'
        return role.description?.[currentLang] || role.description?.en || role.role_code
    }

    const handleImageChange = async e => {
        const file = e.target.files[0]
        if (file) {
            const fileName = file.name
            const fileSize = file.size
            const fileExtension = fileName.split('.').pop().toLowerCase()
            const maxSizeBytes = 2 * 1024 * 1024 // 2 MB in bytes
            const fileSupported = ['png', 'jpeg', 'jpg', 'gif']

            if (fileSize > maxSizeBytes) {
                setImageUploadError('File size exceeds the maximum limit of 2MB.')
                return
            }

            if (!fileSupported.includes(fileExtension)) {
                setImageUploadError('File type not supported.')
                return
            }

            const s3Key = generateUniqueS3Key('private/users', fileName)
            try {
                const uploadResult = await uploadData({
                    path: s3Key,
                    data: file,
                    name: fileName,
                    mime_type: file.type,
                    options: {
                        accessLevel: 'public',
                    },
                })

                const result = {
                    ...(await uploadResult.result),
                    name: fileName,
                    mime_type: file.type,
                }

                setProfileImage(result)
                setImageUploadError('')
            } catch (err) {
                console.error(err)
                setImageUploadError('Error uploading the file.')
            }
        }
    }

    const handleDocumentUpload = async (e, documentType) => {
        const file = e.target.files[0]
        if (file) {
            const fileName = file.name
            const fileSize = file.size
            const fileExtension = fileName.split('.').pop().toLowerCase()
            const maxSizeBytes = 10 * 1024 * 1024 // 10 MB in bytes
            const fileSupported = ['pdf', 'png', 'jpeg', 'jpg']

            const setError = documentType === 'PASSPORT' ? setPassportUploadError : setNationalIdUploadError
            const setAttachment = documentType === 'PASSPORT' ? setPassportAttachment : setNationalIdAttachment
            const setUploading = documentType === 'PASSPORT' ? setPassportUploading : setNationalIdUploading

            if (fileSize > maxSizeBytes) {
                setError('File size exceeds the maximum limit of 10MB.')
                return
            }

            if (!fileSupported.includes(fileExtension)) {
                setError('File type not supported. Only PDF, JPEG, PNG are allowed.')
                return
            }

            const s3Key = generateUniqueS3Key('private/users/documents', fileName)
            setUploading(true)
            setError('')

            try {
                const uploadResult = await uploadData({
                    path: s3Key,
                    data: file,
                    name: fileName,
                    mime_type: file.type,
                    options: {
                        accessLevel: 'public',
                    },
                })

                const result = {
                    ...(await uploadResult.result),
                    name: fileName,
                    mime_type: file.type,
                    size: fileSize,
                }

                setAttachment(result)
                setError('')
            } catch (err) {
                console.error(err)
                setError('Error uploading the file.')
            } finally {
                setUploading(false)
            }
        }
    }

    const handleBack = () => {
        CreateUserFormValidation.resetForm()
        navigate('/users')
    }

    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowErrDialog(false)
    }

    const [createAdminUserInfo, { loading: createUserLoading }] = useMutation(CREATE_ADMIN_USER, {
        onCompleted: data => {
            if (data?.createAdminUser.code === '200' && data?.createAdminUser.success === true) {
                CreateUserFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                CreateUserFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setCreateAdminUserError(data?.createAdminUser.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            CreateUserFormValidation.setSubmitting(false)
            setCreateAdminUserError(error.message)
            setShowErrDialog(true)
        },
    })

    const [updateAdminUser, { loading: updateUserLoading }] = useMutation(UPDATE_ADMIN_USER, {
        onCompleted: data => {
            if (data?.updateAdminUser.code === '200' && data?.updateAdminUser.success === true) {
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                setShowErrDialog(true)
                setCreateAdminUserError(data?.updateAdminUser.message)
            }
        },
        onError: error => {
            setShowErrDialog(true)
            setCreateAdminUserError(error.message)
        },
    })

    useEffect(() => {
        if (isEditMode && userData?.profile_photo) {
            setProfileImage(userData.profile_photo)
        }
        // Load existing attachments in edit mode
        if (isEditMode && userData?.additional_info?.attachments) {
            const passportDoc = userData.additional_info.attachments.find(a => a.document_type === 'PASSPORT')
            const nationalIdDoc = userData.additional_info.attachments.find(a => a.document_type === 'NATIONAL_ID')

            if (passportDoc?.document) {
                setPassportAttachment({
                    name: passportDoc.document.file_name,
                    path: passportDoc.document.s3_key,
                    size: passportDoc.document.size,
                    mime_type: passportDoc.document.mime_type,
                    isExisting: true, // Mark as existing document
                })
            }

            if (nationalIdDoc?.document) {
                setNationalIdAttachment({
                    name: nationalIdDoc.document.file_name,
                    path: nationalIdDoc.document.s3_key,
                    size: nationalIdDoc.document.size,
                    mime_type: nationalIdDoc.document.mime_type,
                    isExisting: true, // Mark as existing document
                })
            }
        }
    }, [isEditMode, userData])

    const CreateUserFormValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            email: userData?.email || '',
            family_name: userData?.family_name || '',
            given_name: userData?.given_name || '',
            locale: userData?.locale || 'en',
            phone_number: userData?.phone_number?.startsWith('+')
                ? userData?.phone_number.substring(1)
                : userData?.phone_number || '',
            userRoles: userData?.userRoles || [],
            passport_number:
                userData?.additional_info?.attachments?.find(a => a.document_type === 'PASSPORT')?.document_number ||
                '',
            national_id_number:
                userData?.additional_info?.attachments?.find(a => a.document_type === 'NATIONAL_ID')?.document_number ||
                '',
        },
        validationSchema: Yup.object().shape({
            email: Yup.string()
                .email(t('Invalid email'))
                .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, t('Invalid email format'))
                .required(t('Email is required')),
            family_name: Yup.string().required(t('Last name is required')),
            given_name: Yup.string().required(t('First name is required')),
            locale: Yup.string().required(t('This field is required')),
            phone_number: Yup.string()
                .required(t('Phone number is required'))
                .test('is-valid-phone', t('Invalid phone number'), value => isPhoneValid(value)),
            passport_number: Yup.string(), // Optional
            national_id_number: Yup.string(), // Optional
            userRoles: Yup.array()
                .of(Yup.string().required(t('Skill is required')))
                .min(1, t('At least one role is required')),
        }),
        onSubmit: values => {
            // Attachments are optional - only validate if a document number is provided
            if (!isEditMode) {
                // If passport number is provided but no attachment, show error
                if (values.passport_number && !passportAttachment) {
                    setPassportUploadError(t('Passport document is required if passport number is provided'))
                    return
                }
                // If national ID number is provided but no attachment, show error
                if (values.national_id_number && !nationalIdAttachment) {
                    setNationalIdUploadError(t('National ID document is required if ID number is provided'))
                    return
                }
            }
            submitCreateUser(values)
            setSubmitLoading(true)
        },
    })

    const submitCreateUser = async userInfo => {
        try {
            CreateUserFormValidation.setSubmitting(true)
            setSubmitLoading(true)

            if (isEditMode) {
                // Check if new documents were uploaded (not existing ones)
                // const hasNewPassport = passportAttachment && !passportAttachment.isExisting
                // const hasNewNationalId = nationalIdAttachment && !nationalIdAttachment.isExisting

                // Prepare attachments array if new documents were uploaded
                const attachments = []

                // Include passport if it exists (new or existing)
                if (passportAttachment) {
                    attachments.push({
                        document_number: userInfo.passport_number || null,
                        document_type: 'PASSPORT',
                        document: {
                            file_name: passportAttachment.name,
                            size: passportAttachment.size,
                            mime_type: passportAttachment.mime_type,
                            s3_key: passportAttachment.path,
                        },
                    })
                }

                // Include national ID if it exists (new or existing)
                if (nationalIdAttachment) {
                    attachments.push({
                        document_number: userInfo.national_id_number || null,
                        document_type: 'NATIONAL_ID',
                        document: {
                            file_name: nationalIdAttachment.name,
                            size: nationalIdAttachment.size,
                            mime_type: nationalIdAttachment.mime_type,
                            s3_key: nationalIdAttachment.path,
                        },
                    })
                }

                // Add new passport attachment if available
                // if (hasNewPassport) {
                //     // Only include document number if provided
                //     attachments.push({
                //         document_number: userInfo.passport_number || null,
                //         document_type: 'PASSPORT',
                //         document: {
                //             file_name: passportAttachment.name,
                //             size: passportAttachment.size,
                //             mime_type: passportAttachment.mime_type,
                //             s3_key: passportAttachment.path,
                //         },
                //     })
                // }

                // Add new national ID attachment if available
                // if (hasNewNationalId) {
                //     // Only include document number if provided
                //     attachments.push({
                //         document_number: userInfo.national_id_number || null,
                //         document_type: 'NATIONAL_ID',
                //         document: {
                //             file_name: nationalIdAttachment.name,
                //             size: nationalIdAttachment.size,
                //             mime_type: nationalIdAttachment.mime_type,
                //             s3_key: nationalIdAttachment.path,
                //         },
                //     })
                // }

                const updateData = {
                    userInfo: {
                        family_name: userInfo.family_name,
                        given_name: userInfo.given_name,
                        locale: userInfo.locale,
                        // Add '+' if not present
                        phone_number: userInfo.phone_number.startsWith('+')
                            ? userInfo.phone_number
                            : '+' + userInfo.phone_number,
                    },
                    userRoles: userInfo.userRoles,
                    userId: userData.id,
                    // Always send additionalInfo with attachments array (even if empty)
                    additionalInfo: {
                        job_title: null,
                        document_number: null,
                        document_type_code: null,
                        document: null,
                        profile_photo: null,
                        attachments: attachments,
                    },
                }

                updateAdminUser({
                    variables: updateData,
                })
            } else {
                // Prepare attachments array
                const attachments = []

                // Add passport attachment if available
                if (passportAttachment) {
                    // Only include document number if provided
                    attachments.push({
                        document_number: userInfo.passport_number || null,
                        document_type: 'PASSPORT',
                        document: {
                            file_name: passportAttachment.name,
                            size: passportAttachment.size,
                            mime_type: passportAttachment.mime_type,
                            s3_key: passportAttachment.path,
                        },
                    })
                }

                // Add national ID attachment if available
                if (nationalIdAttachment) {
                    // Only include document number if provided
                    attachments.push({
                        document_number: userInfo.national_id_number || null,
                        document_type: 'NATIONAL_ID',
                        document: {
                            file_name: nationalIdAttachment.name,
                            size: nationalIdAttachment.size,
                            mime_type: nationalIdAttachment.mime_type,
                            s3_key: nationalIdAttachment.path,
                        },
                    })
                }

                const formattedData = {
                    additionalInfo: {
                        job_title: null,
                        document_number: null,
                        document_type_code: null,
                        document: null,
                        profile_photo: profileImage
                            ? {
                                  file_name: profileImage.name,
                                  size: profileImage.size,
                                  mime_type: profileImage.mime_type,
                                  s3_key: profileImage.path,
                              }
                            : null,
                        attachments: attachments.length > 0 ? attachments : null,
                    },
                    userRoles: userInfo.userRoles,
                    user: {
                        email: userInfo.email,
                        family_name: userInfo.family_name,
                        given_name: userInfo.given_name,
                        locale: userInfo.locale,
                        phone_number: userInfo.phone_number,
                    },
                }
                createAdminUserInfo({
                    variables: formattedData,
                })
            }
        } catch (error) {
            CreateUserFormValidation.setSubmitting(false)
            setSubmitLoading(false)
        }
    }

    const handleRoleSwitchChange = role => {
        const { userRoles } = CreateUserFormValidation.values
        if (userRoles.includes(role)) {
            CreateUserFormValidation.setFieldValue(
                'userRoles',
                userRoles.filter(r => r !== role)
            )
        } else {
            CreateUserFormValidation.setFieldValue('userRoles', [...userRoles, role])
        }
    }

    return (
        <>
            <div className="mb-8 flex justify-between items-center">
                <button
                    type="button"
                    onClick={handleBack}
                    className="inline-flex items-center gap-x-0.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back')}
                </button>
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={handleBack}
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        disabled={CreateUserFormValidation.isSubmitting}
                        onClick={CreateUserFormValidation.handleSubmit}
                        type="button"
                        className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    >
                        {t('Save')}
                    </button>
                </div>
            </div>
            <form onSubmit={CreateUserFormValidation.handleSubmit}>
                <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                                {t('Personal Information')}
                            </h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {t('Personal Information of the user for contact purpose.')}
                            </p>
                        </div>

                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                            {!isEditMode && (
                                <div className="col-span-full">
                                    <div className="flex flex-col items-start justify-center mt-5 mb-2">
                                        <div className="w-32 h-32 relative">
                                            {profileImage ? (
                                                <ImageDisplay
                                                    s3Key={profileImage.path}
                                                    file={profileImage.data}
                                                    classStyle="w-full h-full object-cover rounded-full shadow-lg"
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gray-200 rounded-full flex items-center justify-center">
                                                    <span className="text-gray-400">{t('No Image')}</span>
                                                </div>
                                            )}
                                            {profileImage && (
                                                <button
                                                    type="button"
                                                    className="absolute bottom-[5px] right-[-8px] mt-2 items-center justify-center rounded-full bg-white border-2 border-solid border-red px-2 py-2 text-red text-md font-semibold shadow-sm hover:bg-red-600"
                                                >
                                                    <TrashIcon
                                                        onClick={() => setProfileImage(null)}
                                                        className="w-5 h-5"
                                                    />
                                                </button>
                                            )}
                                        </div>
                                        <label className="mt-1 flex flex-col items-center pt-2 bg-blue-500 text-primary rounded-lg cursor-pointer hover:bg-blue-600">
                                            <span className="inline-flex items-center rounded-md bg-gray-50 px-4 py-2 text-sm font-medium text-primary ring-1 ring-inset ring-gray-500/10">
                                                <ArrowUpTrayIcon className="w-4 h-4 me-2" /> {t('Upload Photo')}
                                            </span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={handleImageChange}
                                            />
                                        </label>
                                        {imageUploadError && (
                                            <p className="text-error text-xs mt-1">{imageUploadError}</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="given_name"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('First Name')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="given_name"
                                        name="given_name"
                                        type="text"
                                        placeholder="Enter here"
                                        value={CreateUserFormValidation.values.given_name}
                                        onChange={CreateUserFormValidation.handleChange}
                                        onBlur={CreateUserFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                </div>
                                {CreateUserFormValidation.touched.given_name &&
                                    CreateUserFormValidation.errors.given_name && (
                                        <p className="text-error text-xs mt-1">
                                            {CreateUserFormValidation.errors.given_name}
                                        </p>
                                    )}
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="family_name"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Last name')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="family_name"
                                        name="family_name"
                                        type="text"
                                        placeholder="Enter here"
                                        value={CreateUserFormValidation.values.family_name}
                                        onChange={CreateUserFormValidation.handleChange}
                                        onBlur={CreateUserFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                </div>
                                {CreateUserFormValidation.touched.family_name &&
                                    CreateUserFormValidation.errors.family_name && (
                                        <p className="text-error text-xs mt-1">
                                            {CreateUserFormValidation.errors.family_name}
                                        </p>
                                    )}
                            </div>

                            <div className="sm:col-span-3">
                                <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Email address')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        disabled={isEditMode}
                                        placeholder="Enter here"
                                        value={CreateUserFormValidation.values.email}
                                        onChange={CreateUserFormValidation.handleChange}
                                        onBlur={CreateUserFormValidation.handleBlur}
                                        className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 ${
                                            isEditMode ? 'bg-gray-100' : ''
                                        }`}
                                    />
                                </div>
                                {CreateUserFormValidation.touched.email && CreateUserFormValidation.errors.email && (
                                    <p className="text-error text-xs mt-1">{CreateUserFormValidation.errors.email}</p>
                                )}
                            </div>

                            <div className="sm:col-span-3">
                                <div className="w-full">
                                    <label
                                        htmlFor="phone_number"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Phone Number')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <div className="mt-0">
                                        <PhoneInput
                                            name="phone_number"
                                            id="phone_number"
                                            defaultCountry="bh"
                                            value={CreateUserFormValidation.values.phone_number}
                                            onChange={phone => {
                                                CreateUserFormValidation.setFieldValue('phone_number', phone)
                                            }}
                                            onBlur={CreateUserFormValidation.handleBlur}
                                            className="block w-full py-1.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                        />
                                    </div>
                                    {CreateUserFormValidation.touched.phone_number &&
                                        CreateUserFormValidation.errors.phone_number && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateUserFormValidation.errors.phone_number}
                                            </p>
                                        )}
                                </div>
                            </div>

                            {/* Passport Section */}
                            <div className="col-span-full">
                                <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                                    <h3 className="text-base font-semibold leading-6 text-gray-900 mb-4">
                                        {t('Passport Details')}
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label
                                                htmlFor="passport_number"
                                                className="block text-sm font-medium leading-6 text-gray-900"
                                            >
                                                {t('Passport Number')}
                                            </label>
                                            <p className="mt-1 text-xs text-gray-500">
                                                {t('Optional for admin, finance, and management roles')}
                                            </p>
                                            <div className="mt-2">
                                                <input
                                                    id="passport_number"
                                                    name="passport_number"
                                                    type="text"
                                                    placeholder="Enter passport number"
                                                    value={CreateUserFormValidation.values.passport_number}
                                                    onChange={CreateUserFormValidation.handleChange}
                                                    onBlur={CreateUserFormValidation.handleBlur}
                                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                />
                                            </div>
                                            {CreateUserFormValidation.touched.passport_number &&
                                                CreateUserFormValidation.errors.passport_number && (
                                                    <p className="text-error text-xs mt-1">
                                                        {CreateUserFormValidation.errors.passport_number}
                                                    </p>
                                                )}
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="passport_attachment"
                                                className="block text-sm font-medium leading-6 text-gray-900"
                                            >
                                                {t('Passport Attachment')}
                                                {!isEditMode && CreateUserFormValidation.values.passport_number && (
                                                    <span className="text-red">*</span>
                                                )}
                                            </label>
                                            <div className="mt-2">
                                                {passportUploading ? (
                                                    <div className="flex flex-col items-center justify-center w-full h-36 border-2 border-primary border-dashed rounded-lg bg-blue-50">
                                                        <div className="flex flex-col items-center justify-center">
                                                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-3"></div>
                                                            <p className="text-sm font-medium text-primary">
                                                                {t('Uploading passport document...')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : passportAttachment ? (
                                                    <div className="rounded-lg border-2 border-green-200 bg-green-50 p-4">
                                                        <div className="flex items-start gap-3">
                                                            <div className="flex-shrink-0">
                                                                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                                                                    <DocumentTextIcon className="w-6 h-6 text-green-600" />
                                                                </div>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                                                                    <p className="text-sm font-semibold text-green-900">
                                                                        {t('Document uploaded successfully')}
                                                                    </p>
                                                                </div>
                                                                <p
                                                                    className="text-sm text-gray-700 truncate font-medium"
                                                                    title={passportAttachment.name}
                                                                >
                                                                    {passportAttachment.name}
                                                                </p>
                                                                {passportAttachment.size && (
                                                                    <p className="text-xs text-gray-500 mt-1">
                                                                        {t('File size')}:{' '}
                                                                        {(
                                                                            passportAttachment.size /
                                                                            1024 /
                                                                            1024
                                                                        ).toFixed(2)}{' '}
                                                                        MB
                                                                    </p>
                                                                )}
                                                            </div>
                                                            {!passportAttachment?.isExisting && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setPassportAttachment(null)
                                                                        setPassportUploadError('')
                                                                    }}
                                                                    className="flex-shrink-0 text-red-600 hover:text-red-800 hover:bg-red-100 p-2 rounded-lg transition-all"
                                                                    title={t('Remove document')}
                                                                >
                                                                    <TrashIcon className="w-5 h-5" />
                                                                </button>
                                                            )}
                                                            {passportAttachment?.isExisting && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setPassportAttachment(null)
                                                                        setPassportUploadError('')
                                                                    }}
                                                                    className="flex-shrink-0 text-blue-600 hover:text-blue-800 hover:bg-blue-100 p-2 rounded-lg transition-all"
                                                                    title={t('Replace document')}
                                                                >
                                                                    <ArrowPathIcon className="w-5 h-5" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <label className="flex flex-col items-center justify-center w-full h-36 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                                                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                            <ArrowUpTrayIcon className="w-10 h-10 mb-3 text-gray-400" />
                                                            <p className="mb-2 text-sm text-gray-600">
                                                                <span className="font-semibold">
                                                                    {t('Click to upload')}
                                                                </span>{' '}
                                                                {t('or drag and drop')}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {t('PDF, JPEG, PNG (MAX. 10MB)')}
                                                            </p>
                                                        </div>
                                                        <input
                                                            id="passport_attachment"
                                                            type="file"
                                                            className="hidden"
                                                            accept=".pdf,.jpg,.jpeg,.png"
                                                            onChange={e => handleDocumentUpload(e, 'PASSPORT')}
                                                            disabled={passportUploading}
                                                        />
                                                    </label>
                                                )}
                                            </div>
                                            {passportUploadError && (
                                                <p className="text-error text-xs mt-1">{passportUploadError}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* National ID Section */}
                            <div className="col-span-full">
                                <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                                    <h3 className="text-base font-semibold leading-6 text-gray-900 mb-4">
                                        {t('National ID Details')}
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label
                                                htmlFor="national_id_number"
                                                className="block text-sm font-medium leading-6 text-gray-900"
                                            >
                                                {t('National ID Number')}
                                            </label>
                                            <p className="mt-1 text-xs text-gray-500">
                                                {t('Optional for admin, finance, and management roles')}
                                            </p>
                                            <div className="mt-2">
                                                <input
                                                    id="national_id_number"
                                                    name="national_id_number"
                                                    type="text"
                                                    placeholder="Enter national ID number"
                                                    value={CreateUserFormValidation.values.national_id_number}
                                                    onChange={CreateUserFormValidation.handleChange}
                                                    onBlur={CreateUserFormValidation.handleBlur}
                                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                />
                                            </div>
                                            {CreateUserFormValidation.touched.national_id_number &&
                                                CreateUserFormValidation.errors.national_id_number && (
                                                    <p className="text-error text-xs mt-1">
                                                        {CreateUserFormValidation.errors.national_id_number}
                                                    </p>
                                                )}
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="national_id_attachment"
                                                className="block text-sm font-medium leading-6 text-gray-900"
                                            >
                                                {t('National ID Attachment')}
                                                {!isEditMode && CreateUserFormValidation.values.national_id_number && (
                                                    <span className="text-red">*</span>
                                                )}
                                            </label>
                                            <div className="mt-2">
                                                {nationalIdUploading ? (
                                                    <div className="flex flex-col items-center justify-center w-full h-36 border-2 border-primary border-dashed rounded-lg bg-blue-50">
                                                        <div className="flex flex-col items-center justify-center">
                                                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-3"></div>
                                                            <p className="text-sm font-medium text-primary">
                                                                {t('Uploading national ID document...')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : nationalIdAttachment ? (
                                                    <div className="rounded-lg border-2 border-green-200 bg-green-50 p-4">
                                                        <div className="flex items-start gap-3">
                                                            <div className="flex-shrink-0">
                                                                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                                                                    <DocumentTextIcon className="w-6 h-6 text-green-600" />
                                                                </div>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                                                                    <p className="text-sm font-semibold text-green-900">
                                                                        {t('Document uploaded successfully')}
                                                                    </p>
                                                                </div>
                                                                <p
                                                                    className="text-sm text-gray-700 truncate font-medium"
                                                                    title={nationalIdAttachment.name}
                                                                >
                                                                    {nationalIdAttachment.name}
                                                                </p>
                                                                {nationalIdAttachment.size && (
                                                                    <p className="text-xs text-gray-500 mt-1">
                                                                        {t('File size')}:{' '}
                                                                        {(
                                                                            nationalIdAttachment.size /
                                                                            1024 /
                                                                            1024
                                                                        ).toFixed(2)}{' '}
                                                                        MB
                                                                    </p>
                                                                )}
                                                            </div>
                                                            {!nationalIdAttachment?.isExisting && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setNationalIdAttachment(null)
                                                                        setNationalIdUploadError('')
                                                                    }}
                                                                    className="flex-shrink-0 text-red-600 hover:text-red-800 hover:bg-red-100 p-2 rounded-lg transition-all"
                                                                    title={t('Remove document')}
                                                                >
                                                                    <TrashIcon className="w-5 h-5" />
                                                                </button>
                                                            )}
                                                            {nationalIdAttachment?.isExisting && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setNationalIdAttachment(null)
                                                                        setNationalIdUploadError('')
                                                                    }}
                                                                    className="flex-shrink-0 text-blue-600 hover:text-blue-800 hover:bg-blue-100 p-2 rounded-lg transition-all"
                                                                    title={t('Replace document')}
                                                                >
                                                                    <ArrowPathIcon className="w-5 h-5" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <label className="flex flex-col items-center justify-center w-full h-36 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                                                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                            <ArrowUpTrayIcon className="w-10 h-10 mb-3 text-gray-400" />
                                                            <p className="mb-2 text-sm text-gray-600">
                                                                <span className="font-semibold">
                                                                    {t('Click to upload')}
                                                                </span>{' '}
                                                                {t('or drag and drop')}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {t('PDF, JPEG, PNG (MAX. 10MB)')}
                                                            </p>
                                                        </div>
                                                        <input
                                                            id="national_id_attachment"
                                                            type="file"
                                                            className="hidden"
                                                            accept=".pdf,.jpg,.jpeg,.png"
                                                            onChange={e => handleDocumentUpload(e, 'NATIONAL_ID')}
                                                            disabled={nationalIdUploading}
                                                        />
                                                    </label>
                                                )}
                                            </div>
                                            {nationalIdUploadError && (
                                                <p className="text-error text-xs mt-1">{nationalIdUploadError}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-x-8 gap-y-4 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">{t('User Profile')}</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {t('This is information which will define the access type and user access the system.')}
                            </p>
                        </div>

                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                            {rolesLoading ? (
                                <div className="sm:col-span-full flex justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                                </div>
                            ) : rolesError ? (
                                <div className="sm:col-span-full text-center py-8">
                                    <p className="text-error">{t('Error loading roles')}</p>
                                </div>
                            ) : (
                                rolesData?.getAdminUserRoles?.roles?.map(role => (
                                    <div key={role.role_code} className="sm:col-span-full">
                                        <div className="overflow-hidden rounded-lg bg-white ring-1 ring-inset ring-gray-300">
                                            <div className="px-4 py-3 sm:p-3">
                                                <Field className="flex items-center justify-between">
                                                    <span className="flex flex-grow flex-col">
                                                        <Label
                                                            as="span"
                                                            passive
                                                            className="text-sm font-medium leading-6 text-gray-900"
                                                        >
                                                            {getRoleDescription(role)} {t('Role')}
                                                        </Label>
                                                        <Description as="span" className="text-sm text-gray-500">
                                                            {role.description?.[i18n.language || 'en'] ||
                                                                role.description?.en ||
                                                                `${t('This role will give access to')} ${role.role_code}`}
                                                        </Description>
                                                    </span>
                                                    <Switch
                                                        checked={CreateUserFormValidation.values.userRoles.includes(
                                                            role.role_code
                                                        )}
                                                        onChange={() => handleRoleSwitchChange(role.role_code)}
                                                        className={`${
                                                            CreateUserFormValidation.values.userRoles.includes(
                                                                role.role_code
                                                            )
                                                                ? 'bg-primary'
                                                                : 'bg-gray-200'
                                                        } group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                                                    >
                                                        <span
                                                            aria-hidden="true"
                                                            className={`${
                                                                CreateUserFormValidation.values.userRoles.includes(
                                                                    role.role_code
                                                                )
                                                                    ? 'translate-x-5'
                                                                    : 'translate-x-0'
                                                            } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                                                        />
                                                    </Switch>
                                                </Field>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                            {CreateUserFormValidation.touched.userRoles &&
                                CreateUserFormValidation.errors.userRoles && (
                                    <p className="text-error text-xs mt-1">
                                        {CreateUserFormValidation.errors.userRoles}
                                    </p>
                                )}

                            <div className="col-span-full">
                                <label htmlFor="locale" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Preferred Language')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <select
                                        id="locale"
                                        name="locale"
                                        onChange={CreateUserFormValidation.handleChange}
                                        onBlur={CreateUserFormValidation.handleBlur}
                                        value={CreateUserFormValidation.values.locale}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:max-w-xs sm:text-sm sm:leading-6"
                                    >
                                        <option value="en">English</option>
                                        <option value="ar">Arabic</option>
                                    </select>
                                </div>
                                {CreateUserFormValidation.touched.locale && CreateUserFormValidation.errors.locale && (
                                    <p className="text-error text-xs mt-1">{CreateUserFormValidation.errors.locale}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-x-3">
                    <button
                        onClick={handleBack}
                        type="button"
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        disabled={CreateUserFormValidation.isSubmitting}
                        type="submit"
                        className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    >
                        {t('Save')}
                    </button>
                </div>
            </form>

            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={createAdminUserError} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title="Success"
                    message={isEditMode ? 'User updated successfully' : 'User Created successfully'}
                    onClose={handleDialogClose}
                />
            )}
            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
